import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { logUnitEvent } from '../data/unitEvents';
import { TRANSMISSIONS, DRIVE_SYSTEMS, STEER_OPTIONS, FUEL_TYPES, POOL_ZONE } from '../data/constants';
import VehicleExtraSpecs, { VEHICLE_CATEGORIES } from './VehicleExtraSpecs';
import { CheckIcon, CameraIcon, UploadIcon } from './Icons';

const STEP_NAMES = ['Identity & Basics', 'Specs', 'Inspection', 'Damage Map', 'Photos'];
const DAMAGE_ZONES = ['Front', 'Rear', 'Left Side', 'Right Side', 'Roof', 'Underbody'];
const MIN_PHOTOS = 6;

function InspectionWizard({ user, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Identity & Basics
  const [vehicleCategory, setVehicleCategory] = useState('passenger_car');
  const [chassis, setChassis] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [engineCode, setEngineCode] = useState('');
  const [displacement, setDisplacement] = useState('');

  // Step 2: Specs
  const [mileage, setMileage] = useState('');
  const [score, setScore] = useState('');
  const [grade, setGrade] = useState('');
  const [fuel, setFuel] = useState('');
  const [transmission, setTransmission] = useState('');
  const [driveSystem, setDriveSystem] = useState('');
  const [color, setColor] = useState('');
  const [steer, setSteer] = useState('');
  const [seats, setSeats] = useState('');
  const [extraSpecs, setExtraSpecs] = useState({});

  // Step 3: Inspection
  const [engineStarts, setEngineStarts] = useState(null);
  const [isMovable, setIsMovable] = useState(null);
  const [missingParts, setMissingParts] = useState('');
  const [accidented, setAccidented] = useState(null);
  const [accidentDetails, setAccidentDetails] = useState('');

  // Step 4: Damage
  const [damageAreas, setDamageAreas] = useState([]);

  // Step 5: Photos
  const [images, setImages] = useState([]);
  const fileRef = useRef();
  const cameraRef = useRef();

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleFiles = async (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!arr.length) return;
    const compressed = await Promise.all(arr.map(f => compressImage(f).catch(() => null)));
    const valid = compressed.filter(Boolean);
    setImages(prev => [...prev, ...valid]);
    for (const c of valid) {
      uploadImage(c).then(url => {
        if (url && url !== c) setImages(prev => prev.map(src => src === c ? url : src));
      }).catch(() => {});
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const toggleDamage = (zone) => {
    setDamageAreas(prev => prev.includes(zone) ? prev.filter(z => z !== zone) : [...prev, zone]);
  };

  const canNext = () => {
    if (step === 1) return chassis.trim().length >= 5 && make.trim();
    if (step === 5) return images.length >= MIN_PHOTOS;
    return true;
  };

  const handleComplete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const list = DB.get('vehicles') || [];
      const id = DB.genId('VEH');
      const sku = `VEH-${(chassis || 'ITEM').substring(0, 6).toUpperCase()}-${String(list.length + 1).padStart(3, '0')}`;
      const newItem = {
        id, sku,
        vehicleCategory: vehicleCategory || 'passenger_car',
        chassis: chassis.trim(), make: make.trim(), model: model.trim(),
        year: year ? Number(year) : null,
        engineCode: engineCode.trim() || null,
        displacement: displacement.trim() || null,
        mileage: mileage ? Number(mileage) : null,
        score: score.trim() || null,
        grade: grade.trim() || null,
        fuel: fuel || null,
        transmission: transmission || null,
        driveSystem: driveSystem || null,
        color: color.trim() || null,
        steer: steer || null,
        seats: seats.trim() || null,
        extraSpecs: Object.keys(extraSpecs).length > 0 ? extraSpecs : null,
        destination_yard: user?.yard || null,
        current_zone: POOL_ZONE,
        status: 'available',
        engine_starts: engineStarts,
        is_movable: isMovable,
        accidented: accidented,
        accident_details: accidentDetails.trim() || null,
        damage_areas: damageAreas.length > 0 ? damageAreas : null,
        missing_parts: missingParts.trim() || null,
        images,
        daysInStock: 0,
        createdAt: new Date().toISOString().split('T')[0],
        created_by: user?.id || null,
        created_by_role: user?.role || null,
        created_by_name: user?.name || null,
      };
      console.log('[inspection] saving images:', images.length);
      const result = await DB.setAsync('vehicles', [...list, newItem]);
      if (result?.error) {
        console.error('[InspectionWizard] Save failed:', result.error);
        alert('Failed to save inspection. Try again.');
        setSaving(false);
        return;
      }
      await logUnitEvent(id, 'vehicles', 'inspected', {
        engine_starts: engineStarts, is_movable: isMovable,
        accidented, damage_areas: damageAreas, missing_parts: missingParts.trim() || null,
      }, user);
      onComplete && onComplete(newItem);
      onClose();
    } catch (err) {
      console.error('[InspectionWizard] Error:', err);
      alert('Something went wrong. Try again.');
      setSaving(false);
    }
  };

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const fieldStyle = {
    width: '100%', padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
    borderRadius: 8, color: 'var(--text1, #e6edf3)', fontSize: 13, fontFamily: 'inherit',
  };
  const labelStyle = { fontSize: 12, color: 'var(--text3)', marginBottom: 4, display: 'block' };
  const selectStyle = { ...fieldStyle, appearance: 'auto' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: 'var(--bg3)' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, var(--blue), var(--cyan))', borderRadius: '0 3px 3px 0', transition: 'width 0.4s ease' }} />
        </div>

        <div className="modal-header">
          <div>
            <h3 className="modal-title">New Inspection</h3>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              Step {step} of {totalSteps} — {STEP_NAMES[step - 1]}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', maxHeight: 'none' }}>

          {/* ── Step 1: Identity & Basics ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>Enter the vehicle identity. Chassis and make are required.</p>
              <div>
                <label style={labelStyle}>Vehicle Category</label>
                <select style={selectStyle} value={vehicleCategory} onChange={e => setVehicleCategory(e.target.value)}>
                  {VEHICLE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Chassis Number *</label>
                <input style={fieldStyle} value={chassis} onChange={e => setChassis(e.target.value)} placeholder="e.g. JZX100-0012345" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Make *</label>
                  <input style={fieldStyle} value={make} onChange={e => setMake(e.target.value)} placeholder="Toyota" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Model</label>
                  <input style={fieldStyle} value={model} onChange={e => setModel(e.target.value)} placeholder="Mark II" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 120 }}>
                  <label style={labelStyle}>Year</label>
                  <input style={fieldStyle} type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="2005" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Engine</label>
                  <input style={fieldStyle} value={engineCode} onChange={e => setEngineCode(e.target.value)} placeholder="e.g. 2JZ-GTE, RB26" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Displacement</label>
                  <input style={fieldStyle} value={displacement} onChange={e => setDisplacement(e.target.value)} placeholder="e.g. 2500cc" />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Specs ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>Vehicle specs — all optional.</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Mileage (km)</label>
                  <input style={fieldStyle} type="number" value={mileage} onChange={e => setMileage(e.target.value)} placeholder="e.g. 85000" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Score</label>
                  <input style={fieldStyle} value={score} onChange={e => setScore(e.target.value)} placeholder="e.g. 4.5, R" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Grade</label>
                  <input style={fieldStyle} value={grade} onChange={e => setGrade(e.target.value)} placeholder="e.g. G, X Limited" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Fuel</label>
                  <select style={selectStyle} value={fuel} onChange={e => setFuel(e.target.value)}>
                    <option value="">Select</option>
                    {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Transmission</label>
                  <select style={selectStyle} value={transmission} onChange={e => setTransmission(e.target.value)}>
                    <option value="">Select</option>
                    {TRANSMISSIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Drive System</label>
                  <select style={selectStyle} value={driveSystem} onChange={e => setDriveSystem(e.target.value)}>
                    <option value="">Select</option>
                    {DRIVE_SYSTEMS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Color</label>
                  <input style={fieldStyle} value={color} onChange={e => setColor(e.target.value)} placeholder="e.g. White, Silver" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Steering</label>
                  <select style={selectStyle} value={steer} onChange={e => setSteer(e.target.value)}>
                    <option value="">Select</option>
                    {STEER_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Seats</label>
                  <input style={fieldStyle} value={seats} onChange={e => setSeats(e.target.value)} placeholder="e.g. 5, 7, 2+2" />
                </div>
              </div>
              <VehicleExtraSpecs
                category={vehicleCategory || 'passenger_car'}
                extraSpecs={extraSpecs}
                onChange={setExtraSpecs}
              />
            </div>
          )}

          {/* ── Step 3: Inspection ── */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>Check the vehicle condition.</p>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, display: 'block' }}>Engine Starts?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(val => (
                    <button key={String(val)} type="button"
                      style={{
                        padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: engineStarts === val ? (val ? '#22c55e' : '#ef4444') : 'var(--bg3)',
                        color: engineStarts === val ? '#fff' : 'var(--text2)',
                        border: engineStarts === val ? '2px solid ' + (val ? '#16a34a' : '#dc2626') : '1px solid var(--border)',
                        boxShadow: engineStarts === val ? '0 0 0 2px ' + (val ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)') : 'none',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => setEngineStarts(val)}>
                      {val ? '✓ Yes' : '✗ No'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, display: 'block' }}>Vehicle Movable?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(val => (
                    <button key={String(val)} type="button"
                      style={{
                        padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: isMovable === val ? (val ? '#22c55e' : '#ef4444') : 'var(--bg3)',
                        color: isMovable === val ? '#fff' : 'var(--text2)',
                        border: isMovable === val ? '2px solid ' + (val ? '#16a34a' : '#dc2626') : '1px solid var(--border)',
                        boxShadow: isMovable === val ? '0 0 0 2px ' + (val ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)') : 'none',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => setIsMovable(val)}>
                      {val ? '✓ Yes' : '✗ No'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, display: 'block' }}>Accidented?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(val => (
                    <button key={String(val)} type="button"
                      style={{
                        padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: accidented === val ? (val ? '#ef4444' : '#22c55e') : 'var(--bg3)',
                        color: accidented === val ? '#fff' : 'var(--text2)',
                        border: accidented === val ? '2px solid ' + (val ? '#dc2626' : '#16a34a') : '1px solid var(--border)',
                        boxShadow: accidented === val ? '0 0 0 2px ' + (val ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)') : 'none',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => setAccidented(val)}>
                      {val ? '✗ Yes' : '✓ No'}
                    </button>
                  ))}
                </div>
                {accidented === true && (
                  <input style={{ ...fieldStyle, marginTop: 10 }} value={accidentDetails}
                    onChange={e => setAccidentDetails(e.target.value)} placeholder="Accident details (e.g. front-end collision)" />
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Missing Parts</label>
                <textarea style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }} value={missingParts}
                  onChange={e => setMissingParts(e.target.value)} placeholder="e.g. Side mirror, spare tire, radio..." />
              </div>
            </div>
          )}

          {/* ── Step 4: Damage Map ── */}
          {step === 4 && (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>Tap all areas with visible damage.</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {DAMAGE_ZONES.map(zone => {
                  const active = damageAreas.includes(zone);
                  return (
                    <button key={zone} type="button" onClick={() => toggleDamage(zone)}
                      style={{
                        padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: active ? '#ef4444' : 'var(--bg3)',
                        color: active ? '#fff' : 'var(--text2)',
                        border: active ? '2px solid #dc2626' : '2px solid var(--border)',
                        transition: 'all 0.15s ease',
                      }}>
                      {zone}
                    </button>
                  );
                })}
              </div>
              {damageAreas.length > 0 && (
                <p style={{ marginTop: 14, fontSize: 13, color: 'var(--text3)' }}>
                  {damageAreas.length} area{damageAreas.length !== 1 ? 's' : ''} marked: {damageAreas.join(', ')}
                </p>
              )}
              {damageAreas.length === 0 && (
                <p style={{ marginTop: 14, fontSize: 13, color: 'var(--text3)', opacity: 0.6 }}>
                  No damage — tap to mark areas, or skip to continue.
                </p>
              )}
            </div>
          )}

          {/* ── Step 5: Photos ── */}
          {step === 5 && (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>
                Capture at least {MIN_PHOTOS} photos: Front, Front 45°, Rear, Rear 45°, Dashboard, Engine.
              </p>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <div className="upload-zone" style={{ flex: 1, padding: '18px 12px', cursor: 'pointer' }} onClick={() => fileRef.current.click()}>
                  <UploadIcon />
                  <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text2)' }}>Upload photos</div>
                  <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
                </div>
                <div className="upload-zone" style={{ flex: 1, padding: '18px 12px', cursor: 'pointer' }} onClick={() => cameraRef.current.click()}>
                  <CameraIcon />
                  <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text2)' }}>Take a photo</div>
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
                </div>
              </div>
              <div style={{
                fontSize: 14, fontWeight: 600, marginBottom: 12,
                color: images.length >= MIN_PHOTOS ? '#22c55e' : 'var(--text3)',
              }}>
                {images.length} / {MIN_PHOTOS} photos captured
                {images.length >= MIN_PHOTOS && ' ✓'}
              </div>
              {images.length > 0 && (
                <div className="upload-preview">
                  {images.map((src, i) => (
                    <div key={i} className="upload-thumb" style={{ position: 'relative' }}>
                      <img src={src} alt="" />
                      <button type="button" className="upload-thumb-rm" onClick={() => removeImage(i)}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={step > 1 ? () => setStep(step - 1) : onClose} disabled={saving}>
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          {step < totalSteps ? (
            <button className="btn btn-primary" disabled={!canNext() || saving} onClick={() => setStep(step + 1)}>
              Next →
            </button>
          ) : (
            <button className="btn btn-success" disabled={!canNext() || saving} onClick={handleComplete}>
              {saving ? 'Saving…' : <><CheckIcon /> Complete Inspection</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InspectionWizard;
